import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { logError } from '../utils/log';

// Helper: normalize venue names to avoid punctuation/case mismatches
const normalizeVenueName = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/gi, '').replace(/restuarant|restaurant/, 'restaurant');
const venueEquals = (a, b) => normalizeVenueName(a) === normalizeVenueName(b);

export const ScheduleBuilderPage = ({ userRole }) => {
  // State for all teams fetched from Firestore
  const [allTeams, setAllTeams] = useState([]);
  // State for all venues fetched from Firestore
  const [venues, setVenues] = useState([]);
  // State for teams selected by the admin for the schedule
  const [selectedTeams, setSelectedTeams] = useState([]);
  // State for the generated schedule
  const [schedule, setSchedule] = useState([]);
  // State for UI messages
  const [message, setMessage] = useState('');
  // State for home/away game breakdown
  const [gameBreakdown, setGameBreakdown] = useState({});
  // Loading state
  const [loading, setLoading] = useState(true);
  // State for schedule settings
  const [scheduleName, setScheduleName] = useState(`Summer ${new Date().getFullYear()} Season`);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [midSeasonEventName, setMidSeasonEventName] = useState('Mid-Season Singles Shoot');
  const [midSeasonEventWeek, setMidSeasonEventWeek] = useState(0); // 0 means no event

  // State for team management forms
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamHomeLocation, setNewTeamHomeLocation] = useState('');
  // State for inline editing
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedLocation, setEditedLocation] = useState('');

  const availableTeams = useMemo(() => {
    return allTeams.filter(team => !team.archived && !selectedTeams.some(st => st.id === team.id));
  }, [allTeams, selectedTeams]);

  // Derived active venues across selected teams and schedule, sorted by boards ascending (1 > 2 > 3)
  const activeVenues = useMemo(() => {
    const venueMap = new Map();
    selectedTeams.forEach(t => {
      const loc = (t.homeLocation || '').trim();
      if (loc && !venueMap.has(normalizeVenueName(loc))) {
        const found = venues.find(v => venueEquals(v.name, loc));
        const boards = found ? (parseInt(found.boards, 10) || 1) : 1;
        venueMap.set(normalizeVenueName(loc), { name: found?.name || loc, boards });
      }
    });
    schedule.forEach(week => {
      if (Array.isArray(week.games)) {
        week.games.forEach(g => {
          if (g.venue && !venueMap.has(normalizeVenueName(g.venue))) {
            const found = venues.find(v => venueEquals(v.name, g.venue));
            const boards = found ? (parseInt(found.boards, 10) || 1) : 1;
            venueMap.set(normalizeVenueName(g.venue), { name: found?.name || g.venue, boards });
          }
        });
      }
    });
    return Array.from(venueMap.values()).sort((a, b) => a.boards - b.boards);
  }, [selectedTeams, venues, schedule]);


  // Fetch all teams from Firestore on component mount
  useEffect(() => {
    if (userRole === 'admin') {
      fetchData();
    }
  }, [userRole]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const teamsCollectionRef = collection(db, "teams");
      const teamsSnapshot = await getDocs(teamsCollectionRef);
      const teamsList = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllTeams(teamsList);

      const venuesCollectionRef = collection(db, "venues");
      const venuesSnapshot = await getDocs(venuesCollectionRef);
      const venuesList = venuesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVenues(venuesList);

    } catch (error) {
      logError("Error fetching teams: ", error);
      setMessage("Failed to load teams from the database.");
    }
    setLoading(false);
  };

    const scanForConflicts = useCallback((scheduleToScan) => {
    // This function is now a no-op since setScheduleConflicts is removed
    return;
    }, []);

  // A simple round-robin pairing algorithm (Circle Method)
  const generateRoundRobinPairings = (teamsToPair) => {
    // Shuffle teams before pairing to randomize BYEs and matchups
    let teams = [...teamsToPair].sort(() => Math.random() - 0.5);
    const n = teams.length;
    if (n % 2 !== 0) {
      // Add a dummy team for odd number of teams
      teams.push({ id: '__BYE__', name: 'BYE', homeLocation: '' });
    }
    const numTeams = teams.length;
    const numRounds = numTeams - 1;
    const pairings = [];

    for (let round = 0; round < numRounds; round++) {
      const roundMatchups = [];
      for (let i = 0; i < numTeams / 2; i++) {
        const team1 = teams[i];
        const team2 = teams[numTeams - 1 - i];
        if (team1 && team2) { // Ensure pairs are valid
          roundMatchups.push([team1, team2]);
        }
      }
      pairings.push(roundMatchups);

      // Rotate teams array, keeping the first team fixed
      const lastTeam = teams.pop();
      teams.splice(1, 0, lastTeam);
    }
    return pairings;
  };

  // Handle interactive team modifications via dropdowns
  const handleTeamChange = (weekIndex, gameIndex, side, newTeamId) => {
    const newSchedule = JSON.parse(JSON.stringify(schedule));
    const week = newSchedule[weekIndex];
    const game = week.games[gameIndex];
    const oldTeamId = side === 'home' ? game.homeTeamId : game.awayTeamId;

    if (oldTeamId === newTeamId) return;

    // Set the new team and mark this match as locked
    if (side === 'home') {
      game.homeTeamId = newTeamId;
    } else {
      game.awayTeamId = newTeamId;
    }
    game.locked = true;

    // Heal current week: check if the new team is already playing in another match
    const otherGameIndex = week.games.findIndex((g, idx) => idx !== gameIndex && !g.eventName && (g.homeTeamId === newTeamId || g.awayTeamId === newTeamId));
    if (otherGameIndex !== -1) {
      const otherGame = week.games[otherGameIndex];
      const otherSide = otherGame.homeTeamId === newTeamId ? 'home' : 'away';
      otherGame[otherSide + 'TeamId'] = oldTeamId;

      // Update venues based on new home assignments
      game.venue = allTeams.find(t => t.id === game.homeTeamId)?.homeLocation || '';
      otherGame.venue = allTeams.find(t => t.id === otherGame.homeTeamId)?.homeLocation || '';

      // Downstream healing: swap corresponding matches in future weeks to preserve perfect round-robin
      const opponentId = side === 'home' ? game.awayTeamId : game.homeTeamId;
      const otherOpponentId = otherGame.homeTeamId === oldTeamId ? otherGame.awayTeamId : otherGame.homeTeamId;

      for (let wIdx = weekIndex + 1; wIdx < newSchedule.length; wIdx++) {
        const dWeek = newSchedule[wIdx];
        if (typeof dWeek.week === 'string' && (dWeek.week.startsWith('Playoff') || dWeek.week === 'Event' || dWeek.week === 'Championship' || dWeek.week === 'Party')) continue;

        const g1Idx = dWeek.games.findIndex(g => !g.eventName && !g.locked &&
          ((g.homeTeamId === newTeamId && g.awayTeamId === opponentId) || (g.homeTeamId === opponentId && g.awayTeamId === newTeamId))
        );
        const g2Idx = dWeek.games.findIndex(g => !g.eventName && !g.locked &&
          ((g.homeTeamId === oldTeamId && g.awayTeamId === otherOpponentId) || (g.homeTeamId === otherOpponentId && g.awayTeamId === oldTeamId))
        );

        if (g1Idx !== -1 && g2Idx !== -1 && g1Idx !== g2Idx) {
          const g1 = dWeek.games[g1Idx];
          const g2 = dWeek.games[g2Idx];

          if (g1.homeTeamId === newTeamId) g1.homeTeamId = oldTeamId;
          else g1.awayTeamId = oldTeamId;

          if (g2.homeTeamId === oldTeamId) g2.homeTeamId = newTeamId;
          else g2.awayTeamId = newTeamId;

          g1.venue = allTeams.find(t => t.id === g1.homeTeamId)?.homeLocation || '';
          g2.venue = allTeams.find(t => t.id === g2.homeTeamId)?.homeLocation || '';
          break;
        }
      }
    } else {
      // New team was not playing. If there is an active BYE in this week, update the BYE
      const newTeamName = allTeams.find(t => t.id === newTeamId)?.name;
      const oldTeamName = allTeams.find(t => t.id === oldTeamId)?.name;
      if (week.byes && week.byes.includes(newTeamName)) {
        week.byes = week.byes.map(b => b === newTeamName ? oldTeamName : b);
      }
      game.venue = allTeams.find(t => t.id === game.homeTeamId)?.homeLocation || '';
    }

    setSchedule(newSchedule);
    scanForConflicts(newSchedule);
  };

  // Generate the full schedule
  const generateSchedule = () => {
    setMessage("");
    setSchedule([]);
    setGameBreakdown({});

    const teamCount = selectedTeams.length;
    if (teamCount < 2) {
      setMessage("A schedule requires at least 2 teams.");
      return;
    }

    // Check if any team lacks a home location
    const teamsWithoutVenue = selectedTeams.filter(t => !t.homeLocation || !t.homeLocation.trim());
    if (teamsWithoutVenue.length > 0) {
      setMessage(`Cannot generate schedule: The following team(s) have no home venue assigned: ${teamsWithoutVenue.map(t => t.name).join(', ')}.`);
      return;
    }

    // Retrieve and preserve existing locked matches
    const lockedGamesByWeek = {};
    schedule.forEach(week => {
      if (week.games) {
        const locked = week.games.filter(g => g.locked && selectedTeams.some(st => st.id === g.homeTeamId) && selectedTeams.some(st => st.id === g.awayTeamId));
        if (locked.length > 0) {
          lockedGamesByWeek[week.week] = locked;
        }
      }
    });

    // Build venue capacity mapping from fetched venues (defaulting to 1 board if unspecified)
    const venueCapacities = {};
    selectedTeams.forEach(team => {
      const loc = (team.homeLocation || '').trim();
      const normLoc = normalizeVenueName(loc);
      if (normLoc && !venueCapacities[normLoc]) {
        const foundVenue = venues.find(v => venueEquals(v.name, loc));
        const boards = foundVenue ? (parseInt(foundVenue.boards, 10) || 1) : 1;
        venueCapacities[normLoc] = boards;
      }
    });

    const numMatchesPerWeek = Math.floor(teamCount / 2);
    const totalCapacity = Object.values(venueCapacities).reduce((sum, b) => sum + b, 0);
    if (totalCapacity < numMatchesPerWeek) {
      setMessage(`Total board capacity across venues (${totalCapacity}) is less than required matches per week (${numMatchesPerWeek}). Please add more boards or venues.`);
      return;
    }

    // Solver: Backtracking allocator for one week's matchups
    // Favors smaller-board venues (1 > 2 > 3) and balances season home/away games
    function solveWeekAssignments(matchups, capacities, seasonHomeCount, seasonAwayCount) {
      const currentUsage = {};
      for (const v in capacities) currentUsage[v] = 0;
      const result = [];

      function backtrack(index) {
        if (index === matchups.length) return true;

        const [teamA, teamB] = matchups[index];
        const normA = normalizeVenueName(teamA.homeLocation);
        const normB = normalizeVenueName(teamB.homeLocation);

        const options = [];

        if (normA && (capacities[normA] || 0) > (currentUsage[normA] || 0)) {
          options.push({
            home: teamA,
            away: teamB,
            venue: teamA.homeLocation,
            normVenue: normA,
            boards: capacities[normA] || 99,
            hDiff: (seasonHomeCount[teamA.id] || 0) - (seasonAwayCount[teamA.id] || 0)
          });
        }

        if (normB && (capacities[normB] || 0) > (currentUsage[normB] || 0)) {
          options.push({
            home: teamB,
            away: teamA,
            venue: teamB.homeLocation,
            normVenue: normB,
            boards: capacities[normB] || 99,
            hDiff: (seasonHomeCount[teamB.id] || 0) - (seasonAwayCount[teamB.id] || 0)
          });
        }

        // Prioritize smaller boards first (1 > 2 > 3) and teams currently needing home games
        options.sort((o1, o2) => {
          const cost1 = o1.hDiff * 3 + o1.boards * 2;
          const cost2 = o2.hDiff * 3 + o2.boards * 2;
          return cost1 - cost2;
        });

        for (const opt of options) {
          currentUsage[opt.normVenue] = (currentUsage[opt.normVenue] || 0) + 1;
          result.push({
            homeTeamId: opt.home.id,
            awayTeamId: opt.away.id,
            venue: opt.venue
          });

          if (backtrack(index + 1)) return true;

          result.pop();
          currentUsage[opt.normVenue]--;
        }

        return false;
      }

      if (backtrack(0)) return result;
      return null;
    }

    const MAX_ATTEMPTS = 3000;
    let attempt = 0;
    let success = false;
    let finalSchedule = [];
    let finalBreakdown = {};

    while (attempt < MAX_ATTEMPTS && !success) {
      attempt++;
      const teamsForPairing = [...selectedTeams];
      let allPairings = generateRoundRobinPairings(teamsForPairing);
      // Shuffle round order to find an optimal fit
      allPairings = allPairings.sort(() => Math.random() - 0.5);

      const generatedSchedule = [];
      const teamHomeGames = {};
      selectedTeams.forEach(t => teamHomeGames[t.id] = { home: 0, away: 0 });

      // Safer date parsing to avoid timezone issues with YYYY-MM-DD format
      let currentDate = new Date(startDate.replace(/-/g, '/'));
      currentDate.setDate(currentDate.getDate() + (2 - currentDate.getDay() + 7) % 7);

      let failed = false;

      for (let week = 0; week < allPairings.length; week++) {
        const weekMatchups = [...allPairings[week]];
        const weekNum = week + 1;
        const lockedForThisWeek = lockedGamesByWeek[weekNum] || [];

        const assignedTeamIds = new Set();
        const weekGames = [];

        // Allocate locked matches first
        lockedForThisWeek.forEach(g => {
          weekGames.push({
            homeTeamId: g.homeTeamId,
            awayTeamId: g.awayTeamId,
            venue: g.venue,
            locked: true
          });
          assignedTeamIds.add(g.homeTeamId);
          assignedTeamIds.add(g.awayTeamId);
        });

        const byes = [];
        const unassignedOrphans = [];
        const activeMatchups = [];

        weekMatchups.forEach(([teamA, teamB]) => {
          if (teamA.name === "BYE" || teamB.name === "BYE" || teamA.id === '__BYE__' || teamB.id === '__BYE__') {
            const activeTeam = (teamA.name !== "BYE" && teamA.id !== '__BYE__') ? teamA : teamB;
            if (assignedTeamIds.has(activeTeam.id)) {
              unassignedOrphans.push({ id: '__BYE__', name: 'BYE', homeLocation: '' });
            } else {
              byes.push(activeTeam.name);
              assignedTeamIds.add(activeTeam.id);
            }
          } else {
            const aAssigned = assignedTeamIds.has(teamA.id);
            const bAssigned = assignedTeamIds.has(teamB.id);

            if (aAssigned && bAssigned) {
              // Matchup is already fully accounted for in locked matches
            } else if (aAssigned) {
              unassignedOrphans.push(teamB);
            } else if (bAssigned) {
              unassignedOrphans.push(teamA);
            } else {
              activeMatchups.push([teamA, teamB]);
            }
          }
        });

        // Pair up unassigned orphans left from locked assignments (including BYE placeholder)
        for (let i = 0; i < unassignedOrphans.length; i += 2) {
          const t1 = unassignedOrphans[i];
          const t2 = unassignedOrphans[i + 1];
          if (t1 && t2) {
            if (t1.id === '__BYE__') {
              byes.push(t2.name);
              assignedTeamIds.add(t2.id);
            } else if (t2.id === '__BYE__') {
              byes.push(t1.name);
              assignedTeamIds.add(t1.id);
            } else {
              activeMatchups.push([t1, t2]);
            }
          }
        }

        const seasonHome = {};
        const seasonAway = {};
        selectedTeams.forEach(t => {
          seasonHome[t.id] = teamHomeGames[t.id].home;
          seasonAway[t.id] = teamHomeGames[t.id].away;
        });

        // Adjust capacities and counts for locked games
        const adjustedCapacities = { ...venueCapacities };
        lockedForThisWeek.forEach(g => {
          const normLoc = normalizeVenueName(g.venue);
          if (normLoc) {
            adjustedCapacities[normLoc] = Math.max(0, (adjustedCapacities[normLoc] || 1) - 1);
          }
          if (teamHomeGames[g.homeTeamId]) teamHomeGames[g.homeTeamId].home++;
          if (teamHomeGames[g.awayTeamId]) teamHomeGames[g.awayTeamId].away++;
          if (seasonHome[g.homeTeamId] !== undefined) seasonHome[g.homeTeamId]++;
          if (seasonAway[g.awayTeamId] !== undefined) seasonAway[g.awayTeamId]++;
        });

        const solvedGames = solveWeekAssignments(activeMatchups, adjustedCapacities, seasonHome, seasonAway);

        if (!solvedGames) {
          failed = true;
          break;
        }

        solvedGames.forEach(g => {
          teamHomeGames[g.homeTeamId].home++;
          teamHomeGames[g.awayTeamId].away++;
        });

        generatedSchedule.push({
          week: weekNum,
          date: currentDate.toLocaleDateString('en-US'),
          games: [...weekGames, ...solvedGames],
          byes: byes,
        });
        currentDate.setDate(currentDate.getDate() + 7);
      }

      if (failed) {
        continue;
      }

      // Check round-robin constraint: each team plays every other team exactly once
      let isRobinValid = true;
      for (let i = 0; i < selectedTeams.length; i++) {
        const team = selectedTeams[i];
        const opponentCounts = {};
        selectedTeams.forEach(otherTeam => {
          if (otherTeam.id !== team.id) {
            opponentCounts[otherTeam.id] = 0;
          }
        });

        generatedSchedule.forEach(week => {
          if (typeof week.week === 'string' && (week.week.startsWith('Playoff') || week.week === 'Event' || week.week === 'Championship' || week.week === 'Party')) return;
          if (!week.games) return;
          week.games.forEach(g => {
            if (g.homeTeamId === team.id && opponentCounts[g.awayTeamId] !== undefined) {
              opponentCounts[g.awayTeamId]++;
            }
            if (g.awayTeamId === team.id && opponentCounts[g.homeTeamId] !== undefined) {
              opponentCounts[g.homeTeamId]++;
            }
          });
        });

        if (Object.values(opponentCounts).some(count => count !== 1)) {
          isRobinValid = false;
          break;
        }
      }

      if (!isRobinValid) {
        continue;
      }

      // Check balance: aim for max |home - away| <= 2 across season
      let isBalanced = true;
      for (const t of selectedTeams) {
        if (Math.abs(teamHomeGames[t.id].home - teamHomeGames[t.id].away) > 2) {
          isBalanced = false;
          break;
        }
      }
      // Strive for balance for the first 1000 attempts, accept any valid schedule if harder
      if (!isBalanced && attempt < 1000) {
        continue;
      }

      // Insert Mid-Season Event
      if (midSeasonEventWeek > 0 && midSeasonEventWeek < generatedSchedule.length) {
        const eventDate = new Date(generatedSchedule[midSeasonEventWeek - 1].date.replace(/-/g, '/'));
        eventDate.setDate(eventDate.getDate() + 7);
        const event = { week: 'Event', date: eventDate.toLocaleDateString('en-US'), games: [{ eventName: midSeasonEventName }], byes: [] };
        generatedSchedule.splice(midSeasonEventWeek, 0, event);
        for (let i = midSeasonEventWeek + 1; i < generatedSchedule.length; i++) {
          const prevWeekDate = new Date(generatedSchedule[i - 1].date.replace(/-/g, '/'));
          generatedSchedule[i].date = new Date(prevWeekDate.setDate(prevWeekDate.getDate() + 7)).toLocaleDateString('en-US');
        }
      }

      // Add Playoff and Party Weeks
      const finalDate = new Date(generatedSchedule[generatedSchedule.length - 1].date.replace(/-/g, '/'));
      finalDate.setDate(finalDate.getDate() + 7);
      generatedSchedule.push({ week: 'Playoffs Wk 1', date: finalDate.toLocaleDateString('en-US'), games: [{ eventName: 'Playoffs Week 1' }], byes: [] });
      finalDate.setDate(finalDate.getDate() + 7);
      generatedSchedule.push({ week: 'Playoffs Wk 2', date: finalDate.toLocaleDateString('en-US'), games: [{ eventName: 'Playoffs Week 2' }], byes: [] });
      finalDate.setDate(finalDate.getDate() + 7);
      generatedSchedule.push({ week: 'Championship', date: finalDate.toLocaleDateString('en-US'), games: [{ eventName: 'Championship Match' }], byes: [] });
      finalDate.setDate(finalDate.getDate() + 7);
      generatedSchedule.push({ week: 'Party', date: finalDate.toLocaleDateString('en-US'), games: [{ eventName: 'End of Season Party' }], byes: [] });

      // Build breakdown
      const breakdown = {};
      selectedTeams.forEach(t => { breakdown[t.name] = { homeGames: 0, awayGames: 0 }; });
      generatedSchedule.forEach(week => {
        if (typeof week.week === 'string' && (week.week.startsWith('Playoff') || week.week === 'Event' || week.week === 'Championship' || week.week === 'Party')) return;
        week.games.forEach(game => {
          const homeTeam = selectedTeams.find(t => t.id === game.homeTeamId);
          const awayTeam = selectedTeams.find(t => t.id === game.awayTeamId);
          if (homeTeam && breakdown[homeTeam.name]) breakdown[homeTeam.name].homeGames++;
          if (awayTeam && breakdown[awayTeam.name]) breakdown[awayTeam.name].awayGames++;
        });
      });

      // Success!
      success = true;
      finalSchedule = generatedSchedule;
      finalBreakdown = breakdown;
    }

    if (!success) {
      setMessage("Could not generate a valid schedule after multiple attempts. Please try again or adjust venue board capacities.");
      setMessage(`Could not generate a valid schedule after ${attempt} attempts to randomize and fit ALL constraints. Please try again or adjust venue board capacities.`);
      return;
    }

    setSchedule(finalSchedule);
    setGameBreakdown(finalBreakdown);
    scanForConflicts(finalSchedule);
  };

  // Function to download the schedule as a CSV file
  const downloadSchedule = () => {
    if (schedule.length === 0) {
      setMessage('Generate a schedule first to download it.');
      return;
    }
    
    const header = "Week,Home Team,Away Team,Venue\n";
    const rows = schedule.flatMap(week =>
      week.games.map(game =>
        `${week.week},"${game.homeTeam}","${game.awayTeam}","${game.venue}"`
      )
    );
    const csvContent = header + rows.join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'dart-league-schedule.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setMessage('Schedule downloaded successfully!');
  };

  // Reset the application state
  const resetAll = () => {
    setSelectedTeams([]);
    setScheduleName(`Summer ${new Date().getFullYear()} Season`);
    setSchedule([]);
    setGameBreakdown({});
    setMessage('');
  };
  
  // --- Team Management Functions (mimicking TeamSetupPage) ---
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim() || !newTeamHomeLocation.trim()) {
      setMessage('Team name and home location are required.');
      return;
    }
    try {
      const teamsCollectionRef = collection(db, "teams");
      await addDoc(teamsCollectionRef, { name: newTeamName, homeLocation: newTeamHomeLocation, members: [] });
      setNewTeamName('');
      setNewTeamHomeLocation('');
      setMessage(`Team "${newTeamName}" created successfully.`);
      fetchData(); // Refresh the master list
    } catch (error) {
      setMessage("Error creating team.");
      logError("Error creating team: ", error);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (window.confirm("Are you sure you want to permanently delete this team? This cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "teams", teamId));
        // Also remove it from the selected pool if it's there
        setSelectedTeams(prev => prev.filter(t => t.id !== teamId));
        setMessage("Team deleted successfully.");
        fetchData(); // Refresh the master list
      } catch (error) {
        setMessage("Error deleting team.");
        logError("Error deleting team: ", error);
      }
    }
  };

  const handleStartEdit = (team) => {
    setEditingTeamId(team.id);
    setEditedName(team.name);
    setEditedLocation(team.homeLocation);
  };

  const handleCancelEdit = () => {
    setEditingTeamId(null);
    setEditedName('');
    setEditedLocation('');
  };

  const handleSaveEdit = async (teamId) => {
    if (!editedName.trim() || !editedLocation.trim()) {
      setMessage("Team name and location cannot be empty.");
      return;
    }
    try {
      const teamDocRef = doc(db, "teams", teamId);
      await updateDoc(teamDocRef, { name: editedName, homeLocation: editedLocation });
      setMessage("Team updated successfully.");
      handleCancelEdit();
      fetchData();
    } catch (error) {
      setMessage("Error updating team.");
    }
  };

  // handleUpdateMatch is not used; remove to resolve warning


  const handleByeChange = (weekIndex, newByeTeamName) => {
    const newSchedule = JSON.parse(JSON.stringify(schedule));
    const week = newSchedule[weekIndex];
    const originalByeTeamName = week.byes[0];

    if (originalByeTeamName === newByeTeamName) return; // No change

    // Find the match the new BYE team was supposed to play
    const matchToChangeIndex = week.games.findIndex(g => allTeams.find(t => t.id === g.homeTeamId)?.name === newByeTeamName || allTeams.find(t => t.id === g.awayTeamId)?.name === newByeTeamName);
    if (matchToChangeIndex === -1) return;

    const matchToChange = week.games[matchToChangeIndex];
    const originalOpponentId = matchToChange.homeTeamId === allTeams.find(t => t.name === newByeTeamName)?.id ? matchToChange.awayTeamId : matchToChange.homeTeamId;
    const originalByeTeamId = allTeams.find(t => t.name === originalByeTeamName)?.id;

    // The original opponent now plays the original BYE team
    // We'll make the original opponent the home team for simplicity
    matchToChange.homeTeamId = originalOpponentId;
    matchToChange.awayTeamId = originalByeTeamId;
    matchToChange.venue = allTeams.find(t => t.id === originalOpponentId)?.homeLocation;

    // Update the BYE list for the week
    week.byes = [newByeTeamName];

    setSchedule(newSchedule);
    scanForConflicts(newSchedule);
    setMessage(`Schedule updated for Week ${week.week}.`);
  };

  const handleFinalizeSchedule = async () => {
    if (schedule.length === 0) {
      setMessage("Please generate a schedule first.");
      return;
    }
    if (window.confirm(`Are you sure you want to finalize and save the "${scheduleName}" schedule?`)) {
      try {
        const scheduleData = {
          name: scheduleName,
          createdAt: new Date().toISOString(),
          teams: selectedTeams.map(t => t.id), // Store IDs of participating teams
          weeks: schedule,
        };
        // Use scheduleName as the document ID for easy lookup
        await setDoc(doc(db, "schedules", scheduleName), scheduleData);
        setMessage("Schedule finalized and saved successfully!");
      } catch (error) {
        setMessage("Error saving schedule.");
        logError("Error saving schedule: ", error);
      }
    }
  };

  if (userRole !== 'admin') {
    return <p className="text-xl text-danger text-center">Access Denied. Only administrators can view this page.</p>;
  }

  if (loading) {
    return <div className="text-center">Loading teams...</div>;
  }

  return (
    <div className="space-y-8">
        <h1 className="page-title heading-primary-1 text-4xl md:text-5xl font-extrabold text-center">Dart League Schedule</h1>
        <p className="text-center text-lg text-muted">Build and generate your season schedule</p>

        {message && <p className={`text-center mb-4 ${message.startsWith('Error') ? 'text-danger' : 'text-success'}`}>{message}</p>}

        <div className="card p-6 rounded-lg shadow-md">
          <h2 className="heading-primary-1 text-2xl font-bold mb-4">1. Schedule Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="scheduleName" className="block text-sm font-medium text-muted">Schedule Name</label>
              <input type="text" id="scheduleName" value={scheduleName} onChange={(e) => setScheduleName(e.target.value)} className="mt-1 block w-full p-2 border-low border rounded-md surface-low" />
            </div>
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-muted">Season Start Date (select any day in the first week)</label>
              <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 block w-full p-2 border-low border rounded-md surface-low" />
            </div>
            <div>
              <label htmlFor="midSeasonEventName" className="block text-sm font-medium text-muted">Mid-Season Event Name</label>
              <input type="text" id="midSeasonEventName" value={midSeasonEventName} onChange={(e) => setMidSeasonEventName(e.target.value)} className="mt-1 block w-full p-2 border-low border rounded-md surface-low" />
            </div>
            <div>
              <label htmlFor="midSeasonEventWeek" className="block text-sm font-medium text-muted">Insert Event After Week</label>
              <input type="number" id="midSeasonEventWeek" value={midSeasonEventWeek} onChange={(e) => setMidSeasonEventWeek(parseInt(e.target.value, 10) || 0)} className="mt-1 block w-full p-2 border-low border rounded-md surface-low" placeholder="0 for no event" />
            </div>
          </div>

        </div>

        {/* Team Management & Selection Section */}
        <div className="card p-6 rounded-lg shadow-md">
          <h2 className="heading-primary-1 text-2xl font-bold mb-4">Team Pool Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Available Teams Pool */}
            <div>
              <h3 className="text-lg font-semibold text-strong mb-2">2. Available Teams ({availableTeams.length})</h3>
              <ul className="max-h-60 overflow-y-auto space-y-2 surface-low p-2 rounded-md border border-low">
                {availableTeams.map((team) => (
                  editingTeamId === team.id ? (
                    <li key={team.id} className="p-2 rounded-md bg-primary-soft">
                      <input type="text" value={editedName} onChange={(e) => setEditedName(e.target.value)} className="w-full p-1 border border-low rounded-md mb-1 surface-low" />
                      <select value={editedLocation} onChange={(e) => setEditedLocation(e.target.value)} className="w-full p-1 border border-low rounded-md mb-1 surface-low"><option value="">Select Venue</option>{venues.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}</select>
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveEdit(team.id)} className="text-success text-xs font-bold hover:underline">Save</button>
                        <button onClick={handleCancelEdit} className="text-muted text-xs hover:underline">Cancel</button>
                      </div>
                    </li>
                  ) : (
                    <li key={team.id} className="flex justify-between items-center p-2 rounded-md transition hover:opacity-90">
                      <span className="text-strong">{team.name} <span className="text-xs text-muted">({team.homeLocation})</span></span>
                      <div>
                        <button onClick={() => setSelectedTeams(prev => [...prev, team])} className="text-success font-semibold text-sm mr-2 hover:underline">Add</button>
                        <button onClick={() => handleStartEdit(team)} className="text-info text-xs mr-2 hover:underline">Edit</button>
                        <button onClick={() => handleDeleteTeam(team.id)} className="text-danger text-xs hover:underline">Delete</button>
                      </div>
                    </li>
                  )
                ))}
              </ul>
            </div>
            {/* Selected Teams Pool */}
            <div>
              <h3 className="text-lg font-semibold text-strong mb-2">3. Teams in this Schedule ({selectedTeams.length})</h3>
              <ul className="max-h-60 overflow-y-auto space-y-2 surface-low p-2 rounded-md border border-low">
                {selectedTeams.map((team) => (
                  <li key={team.id} className="flex justify-between items-center p-2 rounded-md transition hover:opacity-90">
                    <span className="text-strong">{team.name} <span className="text-xs text-muted">({team.homeLocation})</span></span>
                    <button onClick={() => setSelectedTeams(prev => prev.filter(t => t.id !== team.id))} className="text-danger font-semibold text-sm hover:underline">Remove</button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {/* Create New Team Form */}
          <form onSubmit={handleCreateTeam} className="mt-6 border-t border-low pt-4">
            <h3 className="text-lg font-semibold text-strong mb-2">Create New Team</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <input type="text" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="New Team Name" className="flex-1 p-2 border border-low rounded-md surface-low" required />
              <select value={newTeamHomeLocation} onChange={(e) => setNewTeamHomeLocation(e.target.value)} className="flex-1 p-2 border border-low rounded-md surface-low" required><option value="">Select Home Venue</option>{venues.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}</select>
              <button type="submit" className="btn btn-primary px-4 py-2">Create & Add</button>
            </div>
          </form>
        </div>

        {/* Action Buttons Section */}
        {selectedTeams.length >= 2 && (
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <button
              onClick={generateSchedule}
              className="btn btn-primary p-4 rounded-lg font-bold transition-all duration-200 shadow-lg transform hover:scale-105"
            >
              4. Generate Schedule
            </button>
            {schedule.length > 0 && (
              <button onClick={generateSchedule} className="btn btn-warning p-4 rounded-lg font-bold transition-all duration-200 shadow-lg transform hover:scale-105">
                Re-Generate Schedule
              </button>
            )}
            <button
              onClick={resetAll}
              className="btn btn-secondary p-4 rounded-lg font-bold transition-all duration-200 shadow-lg transform hover:scale-105"
            >
              Start Over
            </button>
          </div>
        )}
        
        {/* Generated Schedule Section */}
        {schedule.length > 0 && (
          <div className="card p-6 rounded-lg shadow-md mt-8">
            <h2 className="heading-primary-1 text-2xl font-bold mb-4 text-center">5. Review Generated Schedule</h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-4">
              <button onClick={downloadSchedule} className="btn btn-info px-4 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                Download CSV
              </button>
              <button onClick={handleFinalizeSchedule} className="btn btn-primary px-4 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                Finalize & Save Schedule
              </button>
            </div>
            {/* Home/Away Game Breakdown Table */}
            <div className="mb-8">
              <h2 className="heading-primary-1 text-2xl font-bold mb-4 text-center">Home/Away Game Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="min-w-max sm:min-w-full rounded-lg overflow-hidden border border-low">
                  <thead className="table-header">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Team Name</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Home Games</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Away Games</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Away@Home</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTeams.map((team, index) => {
                      let homeGames = 0;
                      let awayGames = 0;
                      let awayAtHome = 0;
                      schedule.forEach(week => {
                        if (typeof week.week === 'string' && (week.week.startsWith('Playoff') || week.week === 'Event' || week.week === 'Championship' || week.week === 'Party')) return;
                        week.games.forEach(g => {
                          if (g.homeTeamId === team.id) homeGames++;
                          if (g.awayTeamId === team.id) {
                            awayGames++;
                            if (venueEquals(g.venue, team.homeLocation)) {
                              awayAtHome++;
                            }
                          }
                        });
                      });
                      return (
                        <tr key={team.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-strong border-b border-low">{team.name}</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-muted border-b border-low">{homeGames}</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-muted border-b border-low">{awayGames}</td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-muted border-b border-low">{awayAtHome}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Venue Confirmation Section (moved below Home/Away Breakdown) */}
            <div className="mb-8">
              <h2 className="heading-primary-1 text-xl font-bold text-center mb-2">Venue Confirmation</h2>
              <div className="overflow-x-auto">
                <table className="min-w-max sm:min-w-full rounded-lg overflow-hidden border border-low">
                  <thead className="table-header">
                    <tr>
                      <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Week</th>
                      {activeVenues.map(v => (
                        <th key={v.name} className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">
                          {v.name} ({v.boards} {v.boards === 1 ? 'board' : 'boards'})
                        </th>
                      ))}
                      <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Home Venue OK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((week, idx) => {
                      // Only show for numbered weeks (skip events/playoffs/party)
                      if (typeof week.week !== 'number' && isNaN(parseInt(week.week))) return null;
                      let allHomeVenueOk = true;
                      week.games.forEach(game => {
                        const homeTeam = allTeams.find(t => t.id === game.homeTeamId);
                        if (!homeTeam || !venueEquals(homeTeam.homeLocation, game.venue)) {
                          allHomeVenueOk = false;
                        }
                      });

                      return (
                        <tr key={idx} className={idx % 2 === 1 ? 'row-alt' : ''}>
                          <td className="py-2 px-4 whitespace-nowrap text-sm font-medium text-strong border-b border-low">{week.week}</td>
                          {activeVenues.map(v => {
                            const count = week.games.filter(g => venueEquals(g.venue, v.name)).length;
                            const isOver = count > v.boards;
                            const statusColor = isOver ? 'text-danger font-semibold' : 'text-success font-semibold';
                            return (
                              <td key={v.name} className={`py-2 px-4 whitespace-nowrap text-sm ${statusColor}`}>
                                {count} / {v.boards} {isOver ? '✗' : '✓'}
                              </td>
                            );
                          })}
                          <td className={`py-2 px-4 whitespace-nowrap text-sm ${allHomeVenueOk ? 'text-success font-semibold' : 'text-danger font-semibold'}`}>
                            {allHomeVenueOk ? '✓' : '✗'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-2 text-sm text-muted">
                  <ul className="list-disc ml-6 space-y-1">
                    {activeVenues.map(v => (
                      <li key={v.name}>
                        <span className="font-semibold">{v.name}:</span>{' '}
                        <span className="text-success">Max {v.boards} {v.boards === 1 ? 'match' : 'matches'}</span> per week ({v.boards} {v.boards === 1 ? 'board' : 'boards'})
                      </li>
                    ))}
                    <li><span className="font-semibold">Priority:</span> Venues with fewer boards (1 &gt; 2 &gt; 3 boards) are favored to keep smaller venues utilized during BYE and rotation weeks.</li>
                    <li><span className="font-semibold">Home Venue OK:</span> Match venue corresponds to the designated home team's home location.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="heading-primary-1 text-xl font-bold text-center mb-2">Full Schedule Confirmation</h2>
              <div className="overflow-x-auto">
                <table className="min-w-max sm:min-w-full rounded-lg overflow-hidden border border-low">
                  <thead className="table-header">
                    <tr>
                      <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Team Name</th>
                      <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Total Games</th>
                      <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Status</th>
                      <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-low">Robin Confirmed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTeams.map((team, index) => {
                      const totalGames = (gameBreakdown[team.name]?.homeGames || 0) + (gameBreakdown[team.name]?.awayGames || 0);
                      // Calculate expected games: number of weeks with matches (excluding events/playoffs/party/byes)
                      const expectedGames = schedule.filter(w => Array.isArray(w.games) && w.games.some(g => g.homeTeamId === team.id || g.awayTeamId === team.id)).length;
                      let status = '';
                      if (totalGames === expectedGames && totalGames > 0) {
                        status = <span className="text-success font-semibold">Full</span>;
                      } else if (totalGames < expectedGames) {
                        status = <span className="text-danger font-semibold">Missing</span>;
                      } else if (totalGames > expectedGames) {
                        status = <span className="text-warning font-semibold">Extra</span>;
                      } else {
                        status = <span className="text-muted">None</span>;
                      }

                      let robinConfirmed = true;
                      const opponentCounts = {};
                      selectedTeams.forEach(otherTeam => {
                        if (otherTeam.id !== team.id) {
                          opponentCounts[otherTeam.id] = 0;
                        }
                      });

                      schedule.forEach(week => {
                        if (typeof week.week === 'string' && (week.week.startsWith('Playoff') || week.week === 'Event' || week.week === 'Championship' || week.week === 'Party')) return;
                        week.games.forEach(g => {
                          if (g.homeTeamId === team.id && opponentCounts[g.awayTeamId] !== undefined) {
                            opponentCounts[g.awayTeamId]++;
                          }
                          if (g.awayTeamId === team.id && opponentCounts[g.homeTeamId] !== undefined) {
                            opponentCounts[g.homeTeamId]++;
                          }
                        });
                      });

                      Object.values(opponentCounts).forEach(count => {
                        if (count !== 1) {
                          robinConfirmed = false;
                        }
                      });

                      const robinStatus = robinConfirmed ? (
                        <span className="text-success font-semibold">✓ Confirmed</span>
                      ) : (
                        <span className="text-danger font-semibold">✗ Failed</span>
                      );

                      return (
                        <tr key={team.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                          <td className="py-2 px-4 whitespace-nowrap text-sm font-medium text-strong border-b border-low">{team.name}</td>
                          <td className="py-2 px-4 whitespace-nowrap text-sm text-muted border-b border-low">{totalGames}</td>
                          <td className="py-2 px-4 whitespace-nowrap text-sm border-b border-low">{status}</td>
                          <td className="py-2 px-4 whitespace-nowrap text-sm border-b border-low">{robinStatus}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>


            <div className="text-center my-6">
                <button onClick={generateSchedule} className="btn btn-warning p-4 rounded-lg font-bold transition-all duration-200 shadow-lg transform hover:scale-105">
                    Re-Generate Schedule
                </button>
            </div>

            <div className="space-y-6">
              {schedule.map((week, index) => (
                <div key={index} className="surface-low p-4 rounded-lg shadow-inner border border-low">
                  <h3 className="text-xl font-bold text-center text-strong mb-2">{week.week === 'Event' ? week.games[0].eventName : `Week ${week.week}`} ({week.date})</h3>
                  <div className="space-y-2">
                    {week.games.map((game, gameIndex) => 
                      game.eventName ? (
                        <div key={gameIndex} className="text-center bg-warning-soft p-3 rounded-md">
                          <p className="font-bold text-warning">{game.eventName}</p>
                        </div>
                      ) : (
                        <div key={gameIndex} className={`flex items-center justify-between surface-low p-3 rounded-md border ${game.locked ? 'border-2 border-dashed border-accent bg-accent-soft shadow-md' : 'border-low'}`}>
                          <div className="flex-grow grid grid-cols-[40%_auto_40%] items-center gap-4">
                            <select
                              value={game.awayTeamId}
                              onChange={(e) => handleTeamChange(index, gameIndex, 'away', e.target.value)}
                              className="text-sm font-semibold subheading-primary-1 text-right border border-low rounded px-2 py-1 surface-low max-w-full"
                            >
                              {selectedTeams.map(t => (
                                <option key={t.id} value={t.id} disabled={t.id === game.homeTeamId}>{t.name}</option>
                              ))}
                            </select>
                            <span className="text-muted text-center">
                              at
                            </span>
                            <select
                              value={game.homeTeamId}
                              onChange={(e) => handleTeamChange(index, gameIndex, 'home', e.target.value)}
                              className="text-sm font-semibold subheading-primary-1 text-left border border-low rounded px-2 py-1 surface-low max-w-full"
                            >
                              {selectedTeams.map(t => (
                                <option key={t.id} value={t.id} disabled={t.id === game.awayTeamId}>{t.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex flex-col items-end gap-1 w-1/3">
                            <p className="text-sm text-muted">Venue: {game.venue}</p>
                            <div className="flex gap-2 mt-1">
                              {/* Lock / Unlock Toggle button */}
                              <button onClick={() => {
                                const newSchedule = JSON.parse(JSON.stringify(schedule));
                                newSchedule[index].games[gameIndex].locked = !newSchedule[index].games[gameIndex].locked;
                                setSchedule(newSchedule);
                                scanForConflicts(newSchedule);
                              }} className={`btn text-xs px-2 py-0.5 rounded-md ${game.locked ? 'btn-warning' : 'btn-secondary'}`} type="button">
                                {game.locked ? '🔒 Locked' : '🔓 Unlocked'}
                              </button>
                              {/* Move Match to Another Week */}
                              <select
                                className="text-xs border border-low rounded px-1 py-0.5 surface-low"
                                value={index}
                                onChange={e => {
                                  const newWeekIndex = parseInt(e.target.value, 10);
                                  if (newWeekIndex !== index) {
                                    const newSchedule = JSON.parse(JSON.stringify(schedule));
                                    const match = newSchedule[index].games.splice(gameIndex, 1)[0];
                                    newSchedule[newWeekIndex].games.push(match);
                                    setSchedule(newSchedule);
                                    scanForConflicts(newSchedule);
                                    setMessage(`Match moved to Week ${newSchedule[newWeekIndex].week}`);
                                  }
                                }}
                              >
                                {schedule.map((w, wIdx) =>
                                  (typeof w.week === 'number' || !isNaN(parseInt(w.week))) &&
                                  <option key={wIdx} value={wIdx}>{w.week}</option>
                                )}
                              </select>
                              {/* Swap Home/Away */}
                              <button
                                className="btn btn-info text-xs px-2 py-0.5"
                                onClick={() => {
                                  const newSchedule = JSON.parse(JSON.stringify(schedule));
                                  const match = newSchedule[index].games[gameIndex];
                                  const temp = match.homeTeamId;
                                  match.homeTeamId = match.awayTeamId;
                                  match.awayTeamId = temp;
                                  // Update venue to new home team
                                  const newHome = allTeams.find(t => t.id === match.homeTeamId);
                                  if (newHome) match.venue = newHome.homeLocation;
                                  setSchedule(newSchedule);
                                  scanForConflicts(newSchedule);
                                  setMessage('Home/Away swapped.');
                                }}
                                type="button"
                              >Swap H/A</button>
                              {/* Delete Match */}
                              <button
                                className="btn btn-danger text-xs px-2 py-0.5"
                                onClick={() => {
                                  if (window.confirm('Delete this match?')) {
                                    const newSchedule = JSON.parse(JSON.stringify(schedule));
                                    newSchedule[index].games.splice(gameIndex, 1);
                                    setSchedule(newSchedule);
                                    scanForConflicts(newSchedule);
                                    setMessage('Match deleted.');
                                  }
                                }}
                                type="button"
                              >Delete</button>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                    {week.byes.length > 0 && (
                      <div className="flex justify-center items-center surface-low border border-low p-2 rounded-md text-sm text-muted">
                        <strong className="mr-2">BYE:</strong>
                        <select value={week.byes[0]} onChange={(e) => handleByeChange(index, e.target.value)} className="surface-low border border-low rounded px-2 py-1">
                          {selectedTeams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default ScheduleBuilderPage;